/**
 * TanStack Query client — sane warm-theme defaults (research-B §5.1).
 *
 * - staleTime: 30s — covers most lists; tabs returning to focus get fresh
 *   data via refetchOnReconnect, which is enough mitigation for the 409
 *   optimistic-lock case (T10.4) without a window-focus thrash.
 * - gcTime: 5min — keeps recently-visited pages warm for back-nav.
 * - refetchOnWindowFocus: false — research-B leans toward true, but the
 *   orchestrator brief explicitly asks for false here. Defer the focus-
 *   refetch decision to the page level (Phase 2 calendar will set its own).
 * - retry: at most 1 retry, AND only for 5xx / network failures. 401/403/
 *   422/409/429 are deterministic — retrying just adds noise and toast spam.
 */
import { QueryClient } from '@tanstack/react-query';

import { ApiError } from './fetcher';

/**
 * ⚠️ `400` ДОБАВЛЕН ВОЛНОЙ Р-55 (П-4 круга ревью), и он того же рода, что соседи.
 *
 * Перечень объясняется абзацем выше — «детерминированное, повтор только шумит».
 * Отказ схемы детерминирован ровно так же: негодный uuid не станет годным со
 * второй попытки. До Р-55 регулярного 400 на ЧИТАЮЩЕМ маршруте не было вовсе
 * (400 приходил с записи, а записи не повторяются в принципе), поэтому его
 * отсутствие в списке ничего не стоило. Волна завела первый: битая ссылка
 * `?view=heatmap&id=<не-uuid>` ходила на сервер ТРИЖДЫ, прежде чем показать
 * «Ссылка испорчена».
 *
 * ⚠️ ЗАПИСЫВАЮЩИХ МАРШРУТОВ ЭТО НЕ КАСАЕТСЯ ВОВСЕ — проверено по этому же
 * файлу, а не предположено: `isNonRetryable` читается ТОЛЬКО из `queries.retry`,
 * а у `mutations` ниже стоит безусловное `retry: false`. Расширение списка не
 * может ни отменить, ни добавить ни одной повторной записи.
 */
function isNonRetryable(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  const s = error.status;
  return s === 400 || s === 401 || s === 403 || s === 404 || s === 409 || s === 422 || s === 429;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        retry: (failureCount, error) => {
          if (isNonRetryable(error)) return false;
          return failureCount < 2;
        },
      },
      mutations: {
        retry: false,
      },
    },
  });
}

/** Singleton client shared by App.tsx via QueryClientProvider. */
export const queryClient = createQueryClient();
